<template>
  <div class="pa-0 ma-0">
    <svg ref="ari-svg" :width="width" :height="height" class="pa-0 ma-0">
      <!-- highlighted region -->
      <g v-if="region != null">
        <polygon :points="regionPoints" fill="#ff3030" stroke="none"></polygon>
      </g>

      <!-- grid -->
      <g>
        <!-- horizontal lines -->
        <line
          v-for="(db, db_index) in yDimensionScale.ticks()"
          :key="`bgh-${db_index}`"
          :x1="xDimensionScale(hzLines[0])"
          :y1="yDimensionScale(db)"
          :x2="xDimensionScale(hzLines[hzLines.length - 1])"
          :y2="yDimensionScale(db)"
          stroke="#000000"
          stroke-width="0.5"
        ></line>

        <!-- major vertical lines -->
        <line
          v-for="(hz, hz_index) in hzLines"
          :key="`bgv-${hz_index}`"
          :x1="xDimensionScale(hz)"
          :y1="yDimensionScale(minDb)"
          :x2="xDimensionScale(hz)"
          :y2="yDimensionScale(maxDb)"
          stroke="#000000"
          stroke-width="0.5"
        ></line>
      </g>

      <!-- highlighted dB values -->
      <g v-for="(db, dbi) in labeledDbVals" :key="'hdbv-' + dbi">
        <line
          :x1="xDimensionScale(hzLines[0])"
          :y1="yDimensionScale(db)"
          :x2="xDimensionScale(hzLines[hzLines.length - 1])"
          :y2="yDimensionScale(db)"
          stroke="#000000"
          stroke-width="2"
        ></line>

        <text
          :x="xDimensionScale(hzLines[hzLines.length - 1]) + 4"
          :y="yDimensionScale(db)"
          dy="0.3rem"
          :style="'position: absolute; font-size: 0.8rem;'"
          text-anchor="start"
          fill="black"
        >
          {{ db }}dB
        </text>
      </g>
    </svg>
    <div v-if="description != ''" class="pa-0 ma-0 pl-2">{{ description }}</div>
  </div>
</template>

<script>
import { scaleLinear, scaleLog } from 'd3-scale';
import { select } from 'd3-selection';

//let HZ_LINES = [125, 250, 500, 1000, 2000, 4000, 8000];
let HZ_LINES = [500, 1000, 2000, 4000, 8000];

// predefined profiles
let PROFILES = {
  // hearing loss yes/no (yes = 4PTA > 20dB)
  'HL - no': {
    region: { 500: { min: -10, max: 20 }, 8000: { min: -10, max: 20 } },
    db_values: [20],
  },
  'HL - yes': {
    region: { 500: { min: 20, max: 120 }, 8000: { min: 20, max: 120 } },
    description: '4PTA > 20dB',
    db_values: [20],
  },

  // Wisconsin Age-Related Hearing Impairment Classification Scale (WARHICS)
  'Level 1': {
    region: { 500: { min: -10, max: 20 }, 8000: { min: -10, max: 20 } },
    db_values: [20],
  },
  'Level 2': {
    region: {
      500: { min: 20, max: 25 },
      1000: { min: 20, max: 25 },
      2000: { min: 20, max: 25 },
      3000: { min: 20, max: 25 },
      4000: { min: 20, max: 30 },
      6000: { min: 20, max: 30 },
      8000: { min: 20, max: 40 },
    },
    db_values: [20],
  },
  'Level 3': {
    region: {
      500: { min: 25, max: 25 },
      1000: { min: 25, max: 25 },
      2000: { min: 25, max: 25 },
      3000: { min: 25, max: 30 },
      4000: { min: 30, max: 45 },
      6000: { min: 30, max: 55 },
      8000: { min: 40, max: 65 },
    },
    db_values: [25, 65],
  },
  'Level 4': {
    region: {
      500: { min: 25, max: 25 },
      1000: { min: 25, max: 25 },
      2000: { min: 25, max: 40 },
      3000: { min: 30, max: 50 },
      4000: { min: 45, max: 70 },
      6000: { min: 55, max: 80 },
      8000: { min: 65, max: 90 },
    },
    db_values: [25, 90],
  },
  'Level 5': {
    region: {
      500: { min: 25, max: 40 },
      1000: { min: 25, max: 40 },
      2000: { min: 40, max: 50 },
      3000: { min: 50, max: 60 },
      4000: { min: 70, max: 85 },
      6000: { min: 80, max: 115 },
      8000: { min: 90, max: 105 },
    },
    db_values: [25, 115],
  },
  'Level 6': {
    region: {
      500: { min: 40, max: 40 },
      1000: { min: 40, max: 40 },
      2000: { min: 50, max: 65 },
      3000: { min: 60, max: 75 },
      4000: { min: 85, max: 100 },
      6000: { min: 115, max: 115 },
      8000: { min: 105, max: 105 },
    },
    db_values: [40, 115],
  },
  'Level 7': {
    region: {
      500: { min: 80, max: 120 },
      1000: { min: 80, max: 120 },
      2000: { min: 80, max: 120 },
      3000: { min: 80, max: 120 },
      4000: { min: 80, max: 120 },
      6000: { min: 80, max: 120 },
      8000: { min: 80, max: 120 },
    },
    description: 'one >80dB',
    db_values: [80],
  },
  'Level 8': {
    region: {
      500: { min: 80, max: 120 },
      1000: { min: 80, max: 120 },
      2000: { min: 80, max: 120 },
      3000: { min: 80, max: 120 },
      4000: { min: 80, max: 120 },
      6000: { min: 80, max: 120 },
      8000: { min: 80, max: 120 },
    },
    description: 'all >80dB',
    db_values: [80],
  },
};

export default {
  components: {},
  directives: {
    xaxis(el, binding) {
      const axisMethod = binding.value;
      select(el)
        .transition()
        .call(axisMethod);
    },
  },
  props: {
    width: {
      type: Number,
      required: false,
      default: 160,
    },
    height: {
      type: Number,
      required: false,
      default: 120,
    },
    // option 1: use predefined profile
    profile: {
      type: String,
      required: false,
      default: null,
    },
    // option 2: specify highlighted region and/or description
    // e.g., { 500: {min: -5, max: 20}, 1000: {min: -5, max: 30} }
    highlightedRegion: {
      type: Object,
      required: false,
      default: () => {},
    },
    regionDescription: {
      type: String,
      required: false,
      default: '',
    },
    labeledDbValues: {
      type: Array,
      required: false,
      default: () => [],
    },
  },
  data() {
    return {
      hzLines: HZ_LINES,
      profiles: PROFILES,
      region: null,
      description: null,
      labeledDbVals: [],
      minDb: -10,
      maxDb: 120,
      minHz: 500,
      maxHz: 11000,
    };
  },
  computed: {
    margin() {
      return {
        top: 5,
        right: 35,
        bottom: 5,
        left: 10,
      };
    },
    xDimensionScale() {
      return scaleLog()
        .domain([this.minHz, this.maxHz])
        .range([this.margin.left, this.width - this.margin.right]);
    },
    yDimensionScale() {
      return scaleLinear()
        .domain([this.minDb, this.maxDb])
        .range([this.margin.top, this.height - this.margin.bottom]);
    },
    regionPoints() {
      let db_vals = Object.keys(this.region);
      let sorted_db_vals = db_vals.sort((a, b) => a - b);
      // min values correspond to the upper boundary of the region
      let upperPoints = sorted_db_vals.map(db => {
        let coords = this.region[db];
        let x = this.xDimensionScale(db);
        let y = this.yDimensionScale(coords['min']);
        return [x, y];
      });

      // max values correspond to the lower boundary
      let lowerPoints = sorted_db_vals.reverse().map(db => {
        let coords = this.region[db];
        let x = this.xDimensionScale(db);
        let y = this.yDimensionScale(coords['max']);
        return [x, y];
      });

      let points = [...upperPoints, ...lowerPoints];
      points.push(upperPoints[0]);
      return points;
    },
  },
  mounted() {
    if (this.profile != null && this.profile in this.profiles) {
      let pf = this.profiles[this.profile];
      this.region = pf.region;
      if ('description' in pf) {
        this.description = pf['description'];
      }
      if ('db_values' in pf) {
        this.labeledDbVals = pf['db_values'];
      }
    } else {
      this.description = this.regionDescription;
      this.region = this.highlightedRegion;
      this.labeledDbVals = this.labeledDbValues;
    }
  },
};
</script>
